import {
  constructorBeacon,
  queryParams,
  usercentricsEvents
} from '@/shared/constants/constants';
import { clearCookie } from '@/shared/utils/cookie';

const debugEnabled = () =>
  new URLSearchParams(window.location.search).has(queryParams.DEBUG_TRACKING);

const log = (...args: unknown[]) => {
  if (debugEnabled()) console.log('🧪 Constructor beacon:', ...args);
};

const readConstructorConsent = async (): Promise<boolean | null> => {
  if (typeof window.UC_UI?.getServicesBaseInfo !== 'function') return null;
  try {
    const services = await Promise.resolve(window.UC_UI.getServicesBaseInfo());
    const svc = services?.find(
      (s) => s?.id === constructorBeacon.UC_SERVICE_ID
    );
    if (!svc) {
      log('service not found in Usercentrics consents');
      return null;
    }
    return !!svc.consent?.status;
  } catch (e) {
    log('error reading UC_UI.getServicesBaseInfo', e);
    return null;
  }
};

const loadConstructorBeacon = (bundleName: string): void => {
  const src = `${constructorBeacon.SCRIPT_BASE_URL}${bundleName}.js`;
  const alreadyLoaded = Array.from(
    document.querySelectorAll<HTMLScriptElement>('script[src]')
  ).some((script) => script.getAttribute('src') === src);
  if (alreadyLoaded) return;

  const script = document.createElement('script');
  script.defer = true;
  script.src = src;
  document.head.appendChild(script);
  log('loaded', src);
};

const isConstructorKey = (key: string | null): key is string =>
  !!key &&
  constructorBeacon.STORAGE_KEY_PREFIXES.some((prefix) =>
    key.startsWith(prefix)
  );

const clearStorage = (getStorage: () => Storage) => {
  try {
    const storage = getStorage();
    const keys = Array.from({ length: storage.length }, (_, i) =>
      storage.key(i)
    ).filter(isConstructorKey);
    keys.forEach((key) => storage.removeItem(key));
  } catch (e) {
    log('could not clear storage', e);
  }
};

const clearCookies = () => {
  try {
    document.cookie
      .split(';')
      .map((cookie) => cookie.split('=')[0].trim())
      .filter(isConstructorKey)
      .forEach(clearCookie);
  } catch (e) {
    log('could not clear cookies', e);
  }
};

// An already-running beacon script cannot be stopped; the cleared keys stay gone from the next page load.
const clearConstructorData = (): void => {
  clearStorage(() => window.localStorage);
  clearStorage(() => window.sessionStorage);
  clearCookies();
  log(
    'declined → cleared storage keys and cookies',
    constructorBeacon.STORAGE_KEY_PREFIXES
  );
};

const reconcileConstructorBeacon = async (
  bundleName: string
): Promise<void> => {
  const consent = await readConstructorConsent();
  if (consent === null) {
    log('consent state unknown, doing nothing');
    return;
  }
  if (consent) loadConstructorBeacon(bundleName);
  else clearConstructorData();
};

const isUsercentricsReady = (): boolean =>
  typeof window.UC_UI?.isInitialized === 'function'
    ? window.UC_UI.isInitialized()
    : !!window.UC_UI;

const initConstructorBeacon = (): void => {
  const bundleName = window.Hobbii?.constructorBeacon?.bundleName;
  if (!bundleName) return;

  const reconcile = () => reconcileConstructorBeacon(bundleName);

  window.addEventListener(usercentricsEvents.UI_INITIALIZED, reconcile);
  // UC_UI can exist before stored consent is resolved; an early read would look like a deny and wipe storage.
  if (isUsercentricsReady()) reconcile();

  window.addEventListener(usercentricsEvents.UC_EVENT, (e) => {
    if ((e as CustomEvent).detail?.event !== usercentricsEvents.CONSENT_STATUS)
      return;
    reconcile();
  });
};

export { initConstructorBeacon };
